import React, { useState } from "react";
import {
  LayoutDashboard,
  BriefcaseBusiness,
  FileText,
  Package,
  MoreHorizontal,
  Users,
  BarChart3,
  CreditCard,
  Settings,
  CalendarDays,
  ClipboardCheck,
  DollarSign,
  Wallet,
  BadgeEuro,
  Calculator,
} from "lucide-react";
import { usePanelTheme } from "../context/themeContext";

export default function MobileBottomNav({ activeTab, onChange, t = {} }) {
  const [showMore, setShowMore] = useState(false);
  const { theme } = usePanelTheme();

  const mainTabs = [
    {
      id: "dashboard",
      label: t.dashboard || "Dashboard",
      icon: LayoutDashboard,
    },
    {
      id: "projects",
      label: t.projects || "Projects",
      icon: BriefcaseBusiness,
    },
    {
      id: "invoice",
      label: t.invoiceTitle || t.invoice || "Invoice",
      icon: FileText,
    },
    {
      id: "materials",
      label: t.materials || "Materials",
      icon: Package,
    },
  ];

  const moreTabs = [
    {
      id: "clients",
      label: t.clients || "Clients",
      icon: Users,
    },
    {
      id: "offers",
      label: t.offers || "Offers",
      icon: FileText,
    },
    {
      id: "kalkulation",
      label: t.kalkulation || "Kalkulation",
      icon: Calculator,
    },
    {
      id: "calendar",
      label: t.calendar || "Calendar",
      icon: CalendarDays,
    },
    {
      id: "timesheets",
      label: t.timesheets || "Timesheets",
      icon: ClipboardCheck,
    },
    {
      id: "projectProfit",
      label: t.projectProfit || "Project Profit",
      icon: BadgeEuro,
    },
    {
      id: "workers",
      label: t.workers || "Workers",
      icon: Users,
    },
    {
      id: "payments",
      label: t.payments || "Payments",
      icon: Wallet,
    },
    {
      id: "income",
      label: t.income || "Income",
      icon: DollarSign,
    },
    {
      id: "expenses",
      label: t.expenses || "Expenses",
      icon: CreditCard,
    },
    {
      id: "reports",
      label: t.reports || "Reports",
      icon: BarChart3,
    },
    {
      id: "settings",
      label: t.settingsTitle || t.settings || "Settings",
      icon: Settings,
    },
  ];

  const isMoreActive = moreTabs.some((tab) => tab.id === activeTab);

  const shellClass =
    theme === "dark"
      ? "border-slate-800/80 bg-slate-950/95"
      : "border-slate-200/80 bg-white/95";

  const inactiveClass =
    theme === "dark"
      ? "text-slate-400 hover:bg-slate-900 hover:text-slate-100"
      : "text-slate-500 hover:bg-slate-50 hover:text-slate-800";

  const activeClass =
    theme === "dark"
      ? "bg-indigo-500/15 text-indigo-300 shadow-sm"
      : "bg-indigo-50 text-indigo-600 shadow-sm";

  const sheetClass =
    theme === "dark" ? "bg-slate-950 text-slate-100" : "bg-white text-slate-900";

  const sheetCardClass =
    theme === "dark"
      ? "bg-slate-900 text-slate-200 hover:bg-slate-800"
      : "bg-slate-50 text-slate-700 hover:bg-slate-100";

  const sheetActiveClass =
    theme === "dark"
      ? "bg-indigo-500/15 text-indigo-300"
      : "bg-indigo-50 text-indigo-600";

  const closeButtonClass =
    theme === "dark"
      ? "bg-slate-900 text-slate-200 hover:bg-slate-800"
      : "bg-slate-100 text-slate-700 hover:bg-slate-200";

  return (
    <>
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 border-t px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-2 backdrop-blur-xl shadow-[0_-10px_35px_rgba(15,23,42,0.08)] xl:hidden ${shellClass}`}
      >
        <div className="mx-auto grid max-w-xl grid-cols-5 gap-2">
          {mainTabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onChange?.(tab.id)}
                className={`flex min-h-[64px] flex-col items-center justify-center rounded-2xl px-2 py-2 text-[11px] font-semibold transition ${
                  active ? activeClass : inactiveClass
                }`}
              >
                <Icon className={`mb-1 h-5 w-5 ${active ? "stroke-[2.4]" : ""}`} />
                <span className="line-clamp-2 text-center leading-tight">
                  {tab.label}
                </span>
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => setShowMore(true)}
            className={`flex min-h-[64px] flex-col items-center justify-center rounded-2xl px-2 py-2 text-[11px] font-semibold transition ${
              isMoreActive ? activeClass : inactiveClass
            }`}
          >
            <MoreHorizontal
              className={`mb-1 h-5 w-5 ${isMoreActive ? "stroke-[2.4]" : ""}`}
            />
            <span className="line-clamp-2 text-center leading-tight">
              {t.more || "More"}
            </span>
          </button>
        </div>
      </div>

      {showMore && (
        <div className="fixed inset-0 z-[60] flex items-end bg-black/40 xl:hidden">
          <div className={`w-full rounded-t-[28px] p-5 shadow-2xl ${sheetClass}`}>
            <div
              className={`mx-auto mb-4 h-1.5 w-14 rounded-full ${
                theme === "dark" ? "bg-slate-700" : "bg-slate-200"
              }`}
            />

            <div className="mb-4 text-center text-base font-bold">
              {t.menu || t.more || "More"}
            </div>

            <div className="grid grid-cols-3 gap-3">
              {moreTabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      onChange?.(tab.id);
                      setShowMore(false);
                    }}
                    className={`flex min-h-[84px] flex-col items-center justify-center gap-2 rounded-2xl p-3 text-sm font-semibold transition ${
                      active ? sheetActiveClass : sheetCardClass
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-center leading-tight">{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setShowMore(false)}
              className={`mt-5 inline-flex h-12 w-full items-center justify-center rounded-2xl text-sm font-semibold transition ${closeButtonClass}`}
            >
              {t.close || "Close"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}